/**
 * Currency on the page (JF 2026-10-13): amounts are kept in USD; the page can show and take them in RMB at the sheet's rate
 * (sheet.fx, else the company default). An amount typed in RMB keeps its RMB figure beside the USD one (`cny`), so a new
 * rate leaves ¥3,000 at ¥3,000.
 */
import { f2, fxOf, isSet, moneyIn, n } from "@ic/engine";
import { useStore, type Cur } from "../store.ts";

/** a converted amount for an input: 3 decimals at most (unit prices of small trims need the third) */
const tidy = (v: number) => String(+v.toFixed(3));

export interface Money {
  cur: Cur; fx: number; sym: string; /** USD → shown currency */ k: number;
  /** a USD amount in the shown currency, 2 decimals, with the symbol */
  fmt(usd: number): string;
  /** the same without the symbol */
  num(usd: number): string;
  /** what an input shows for an amount kept as USD `px` (+ its RMB figure) */
  shown(px: string, cny?: string): string;
  /** what to keep for a typed amount: USD px, and the RMB figure when typed in RMB */
  typed(v: string): { px: string; cny?: string };
  /** amounts without an RMB figure (quoted overrides, freight…): shown / typed in the shown currency, kept in USD */
  plain(usd: string | number | null | undefined): string;
  plainIn(v: string): string;
  /** "$/m" in the shown currency */
  per(unit: string): string;
}

export function moneyFor(cur: Cur, fx: number): Money {
  const k = cur === "CNY" ? fx : 1;
  const sym = cur === "CNY" ? "¥" : "$";
  return {
    cur, fx, sym, k,
    fmt: (usd) => sym + f2(usd * k),
    num: (usd) => f2(usd * k),
    // typed in RMB and shown in USD: 2 decimals (the exact figure is the RMB one, marked ¥ beside it)
    shown: (px, cny) => (cur === "CNY" ? (isSet(cny) ? String(cny) : isSet(px) ? tidy(n(px) * fx) : "") : isSet(cny) && isSet(px) ? f2(n(px)) : px),
    typed: (v) => moneyIn(v, cur, fx),
    plain: (usd) => (!isSet(usd) ? "" : cur === "CNY" ? tidy(n(usd) * fx) : String(usd)),
    plainIn: (v) => (cur === "CNY" && isSet(v.trim()) ? String(+(n(v) / fx).toFixed(6)) : v),
    per: (unit) => `${sym}/${unit}`,
  };
}

/** The money helpers for the sheet being edited, in the currency picked at the top. */
export function useMoney(): Money {
  const cur = useStore((s) => s.cur);
  const sfx = useStore((s) => s.sheet.fx);
  const dfx = useStore((s) => s.defaultFx);
  return moneyFor(cur, fxOf({ fx: sfx }, dfx));
}

/** Store an amount typed in a money field: px in USD, the RMB figure kept when typed in RMB. Mutates. */
export function setMoney(o: { px: string; cny?: string }, v: { px: string; cny?: string }): void {
  o.px = v.px;
  if (v.cny != null) o.cny = v.cny; else delete o.cny;
}

/** Units in the page language (JF 2026-10-13: units switch with the language too). */
const UNIT_ZH: Record<string, string> = { m: "米", yd: "码", kg: "公斤", sqft: "平方英尺", pcs: "个", pc: "件" };
export const unitLabel = (u: string, lang: "zh" | "en") => (lang === "zh" ? UNIT_ZH[u] || u : u);
